import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useBusinessSettingsContext } from '../../hooks/useBusinessSettingsContext'
import { useBusinessRulesContext } from '../../hooks/useBusinessRulesContext'
import { productsService } from '../../services/firestore.service'
import { useCategories, useSubcategories } from '../../hooks/useCategories'
import { getAllSuppliers } from '../../services/suppliers.service'
import { uploadImage } from '../../services/cloudinary.service'
import { generateSerialNumber, generateSkuForProduct } from '../../services/productIdentifiers.service'
import RichTextEditor from '../../components/RichTextEditor'
import Toast from '../../components/Toast'
import SupplierCards from '../../components/admin/SupplierCards'
import VariantStockModal from '../../components/admin/VariantStockModal'

const normalizeList = (input) => {
  if (!input) return []
  if (Array.isArray(input)) return input.map(v => String(v).trim()).filter(Boolean)
  return String(input)
    .split(',')
    .map(v => v.trim())
    .filter(Boolean)
}

function ProductFormFashion() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id && id !== 'nuevo'
  const { currentUser, userData } = useAuth()
  const { activeProfile } = useBusinessSettingsContext()
  const { rules } = useBusinessRulesContext()

  const { categories } = useCategories(true)
  const [selectedCategoryId, setSelectedCategoryId] = useState('')
  const { subcategories } = useSubcategories(selectedCategoryId || null, true)

  const [suppliers, setSuppliers] = useState([])
  const [loadingSuppliers, setLoadingSuppliers] = useState(false)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [notification, setNotification] = useState(null)
  const [errors, setErrors] = useState({})

  const [formData, setFormData] = useState({
    name: '',
    shortDescription: '',
    description: '',
    brand: '',
    categoryId: '',
    subcategoryId: '',
    price: '',
    specialPrice: '',
    active: true,
    images: [],
    sizes: [],
    colors: [],
    colorNames: {},
    color: '',
    material: '',
    fitType: '',
    season: '',
    sizeGuide: '',
    sizeChart: [],
    variants: [],
    supplierIds: [],
    tags: [],
    collection: ''
  })

  const [colorNameInput, setColorNameInput] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [sizeChartRow, setSizeChartRow] = useState({ size: '', chest: '', waist: '', length: '' })
  const [variantColor, setVariantColor] = useState('')
  const [variantSize, setVariantSize] = useState('')
  const [variantPriceDelta, setVariantPriceDelta] = useState('')
  const [variantSku, setVariantSku] = useState('')
  const [variantSerialNumber, setVariantSerialNumber] = useState('')
  const [variantImages, setVariantImages] = useState([])
  const [variantVideos, setVariantVideos] = useState([])
  const [variantVideoInput, setVariantVideoInput] = useState('')
  const [uploadingVariantImages, setUploadingVariantImages] = useState(false)
  const [activeTab, setActiveTab] = useState('general')
  const [editingVariantIndex, setEditingVariantIndex] = useState(null)
  const [editVariantColor, setEditVariantColor] = useState('')
  const [editColorNameInput, setEditColorNameInput] = useState('')
  const [editVariantSize, setEditVariantSize] = useState('')
  const [editVariantPriceDelta, setEditVariantPriceDelta] = useState('')
  const [editVariantSku, setEditVariantSku] = useState('')
  const [editVariantSerialNumber, setEditVariantSerialNumber] = useState('')
  const [editVariantImages, setEditVariantImages] = useState([])
  const [editVariantVideos, setEditVariantVideos] = useState([])
  const [editVariantVideoInput, setEditVariantVideoInput] = useState('')
  const [uploadingEditVariantImages, setUploadingEditVariantImages] = useState(false)
  const [stockModalData, setStockModalData] = useState(null)

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        setLoadingSuppliers(true)
        const data = await getAllSuppliers(true)
        setSuppliers(data || [])
      } catch {
        setSuppliers([])
      } finally {
        setLoadingSuppliers(false)
      }
    }
    loadSuppliers()
  }, [])

  useEffect(() => {
    const loadProduct = async () => {
      if (!isEditing) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        const p = await productsService.getById(id)
        if (!p) {
          setNotification({ type: 'error', message: 'Producto no encontrado' })
          setTimeout(() => setNotification(null), 2500)
          navigate('/admin/productos')
          return
        }
        setFormData(prev => ({
          ...prev,
          name: p.name || '',
          shortDescription: p.shortDescription || '',
          description: p.description || '',
          brand: p.brand || '',
          categoryId: p.categoryId || p.category || '',
          subcategoryId: p.subcategoryId || '',
          price: p.price !== undefined && p.price !== null ? String(p.price) : '',
          specialPrice: p.specialPrice !== undefined && p.specialPrice !== null ? String(p.specialPrice) : '',
          active: p.active !== false,
          images: Array.isArray(p.images) ? p.images : [],
          sizes: Array.isArray(p.sizes) ? p.sizes : [],
          colors: Array.isArray(p.colors) ? p.colors : [],
          colorNames: p.colorNames && typeof p.colorNames === 'object' ? p.colorNames : {},
          color: p.color || '',
          material: p.material || '',
          fitType: p.fitType || '',
          season: p.season || '',
          sizeGuide: p.sizeGuide || '',
          sizeChart: Array.isArray(p.sizeChart) ? p.sizeChart : [],
          variants: Array.isArray(p.variants) ? p.variants : [],
          supplierIds: Array.isArray(p.supplierIds) ? p.supplierIds : (p.supplierId ? [p.supplierId] : []),
          tags: Array.isArray(p.tags) ? p.tags : normalizeList(p.tags),
          collection: p.collection || ''
        }))
        setSelectedCategoryId(p.categoryId || p.category || '')
      } catch {
        setNotification({ type: 'error', message: 'Error cargando producto' })
        setTimeout(() => setNotification(null), 2500)
      } finally {
        setLoading(false)
      }
    }
    loadProduct()
  }, [id, isEditing, navigate])

  useEffect(() => {
    if (formData.categoryId) {
      setSelectedCategoryId(formData.categoryId)
    }
  }, [formData.categoryId])

  const commercialProfile = useMemo(() => {
    if (activeProfile?.rubro === 'indumentaria') {
      return {
        rubro: 'indumentaria',
        subrubro: activeProfile.subrubro || 'femenina',
        designId: activeProfile.designId || 'indumentaria/femenina/v1'
      }
    }
    return {
      rubro: 'muebles',
      subrubro: null,
      designId: activeProfile?.designId || 'muebles/v1'
    }
  }, [activeProfile])

  const handleBasicChange = (e) => {
    const { name, value, type, checked } = e.target
    const nextValue = type === 'checkbox' ? checked : value
    setFormData(prev => ({ ...prev, [name]: nextValue }))
    setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleCategoryChange = (e) => {
    const value = e.target.value
    setFormData(prev => ({ ...prev, categoryId: value, subcategoryId: '' }))
    setSelectedCategoryId(value)
    setErrors(prev => ({ ...prev, categoryId: '' }))
  }

  const handleSupplierToggle = (supplierId) => {
    setFormData(prev => {
      const exists = prev.supplierIds.includes(supplierId)
      return { ...prev, supplierIds: exists ? prev.supplierIds.filter(id => id !== supplierId) : [...prev.supplierIds, supplierId] }
    })
  }

  const addTag = () => {
    const v = tagInput.trim()
    if (!v) return
    setFormData(prev => ({ ...prev, tags: Array.from(new Set([...(prev.tags || []), v])) }))
    setTagInput('')
  }

  const removeTag = (v) => {
    setFormData(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== v) }))
  }

  const addSizeChartRow = () => {
    const row = {
      size: sizeChartRow.size.trim(),
      chest: sizeChartRow.chest.trim(),
      waist: sizeChartRow.waist.trim(),
      length: sizeChartRow.length.trim()
    }
    if (!row.size) return
    setFormData(prev => ({ ...prev, sizeChart: [...(prev.sizeChart || []), row] }))
    setSizeChartRow({ size: '', chest: '', waist: '', length: '' })
  }

  const removeSizeChartRow = (index) => {
    setFormData(prev => ({ ...prev, sizeChart: (prev.sizeChart || []).filter((_, i) => i !== index) }))
  }

  const handleUploadImages = async (files) => {
    if (!files || files.length === 0) return
    setUploadingVariantImages(true)
    try {
      const next = []
      for (const file of Array.from(files)) {
        const uploaded = await uploadImage(file, 'products/variants')
        next.push(uploaded)
      }
      setVariantImages(prev => [...prev, ...next])
    } catch (e) {
      setNotification({ type: 'error', message: e.message || 'Error subiendo imágenes' })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setUploadingVariantImages(false)
    }
  }

  const removeImageAt = (index) => {
    setVariantImages(prev => prev.filter((_, i) => i !== index))
  }

  const addVariantVideo = () => {
    const v = variantVideoInput.trim()
    if (!v) return
    setVariantVideos(prev => Array.from(new Set([...prev, v])))
    setVariantVideoInput('')
  }

  const removeVariantVideo = (index) => {
    setVariantVideos(prev => prev.filter((_, i) => i !== index))
  }

  const handleAddVariant = async () => {
    if (!variantColor) {
      setNotification({ type: 'warning', message: 'Seleccioná un color para la variante' })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    if (!variantSize) {
      setNotification({ type: 'warning', message: 'Indicá el talle para la variante' })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    if (!colorNameInput.trim()) {
      setNotification({ type: 'warning', message: 'Indicá el nombre del color para la variante' })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    if (!variantSku.trim() && rules?.productIdentification?.skuAuto?.enabled !== true) {
      setNotification({ type: 'warning', message: 'El SKU de la variante es requerido' })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    let finalSku = variantSku.trim().toUpperCase()
    if (!finalSku && rules?.productIdentification?.skuAuto?.enabled === true) {
      try {
        const resolvedColorName = colorNameInput.trim() || (formData.colorNames || {})[variantColor] || ''
        const skuPayload = {
          name: formData.name,
          brand: formData.brand,
          collection: formData.collection,
          color: variantColor,
          colorName: resolvedColorName,
          sizes: variantSize ? [variantSize] : []
        }
        const { sku } = await generateSkuForProduct({ ...skuPayload, id }, rules)
        finalSku = sku.toUpperCase()
      } catch (e) {
        setNotification({ type: 'error', message: e?.message || 'No se pudo generar SKU' })
        setTimeout(() => setNotification(null), 5000)
        return
      }
    }

    let finalSerialNumber = String(variantSerialNumber || '').trim()
    if (!finalSerialNumber && rules?.productIdentification?.serialAuto?.enabled === true) {
      try {
        const digits = rules?.productIdentification?.serialAuto?.digits ?? 15
        const serial = await generateSerialNumber(digits)
        finalSerialNumber = serial
      } catch (e) {
        setNotification({ type: 'error', message: e?.message || 'No se pudo generar número de serie' })
        setTimeout(() => setNotification(null), 5000)
        return
      }
    }

    const colorLabel = colorNameInput.trim() || (formData.colorNames || {})[variantColor] || variantColor
    const newVariant = {
      selection: {
        color: variantColor,
        colorLabel,
        size: variantSize
      },
      priceDelta: variantPriceDelta ? Number(variantPriceDelta) : null,
      sku: finalSku,
      serialNumber: finalSerialNumber,
      images: variantImages,
      videos: variantVideos
    }

    setFormData(prev => {
      const nextColors = variantColor ? Array.from(new Set([...(prev.colors || []), variantColor])) : prev.colors
      const nextSizes = variantSize ? Array.from(new Set([...(prev.sizes || []), variantSize])) : prev.sizes
      const nextColorNames = {
        ...(prev.colorNames || {}),
        ...(variantColor ? { [variantColor]: colorLabel } : {})
      }
      const nextPrimary = prev.color || variantColor || ''
      return {
        ...prev,
        colors: nextColors,
        sizes: nextSizes,
        colorNames: nextColorNames,
        color: nextPrimary,
        variants: [...(prev.variants || []), newVariant]
      }
    })

    setVariantColor('')
    setVariantSize('')
    setVariantPriceDelta('')
    setVariantSku('')
    setVariantSerialNumber('')
    setVariantImages([])
    setVariantVideos([])
    setVariantVideoInput('')
    setColorNameInput('')
  }

  const removeVariantAt = (index) => {
    setFormData(prev => ({ ...prev, variants: (prev.variants || []).filter((_, i) => i !== index) }))
  }

  const openStockModal = (variant, variantIndex) => {
    if (!isEditing) return
    setStockModalData({
      variant,
      variantIndex,
      productId: id
    })
  }

  const beginEditVariant = (index) => {
    const v = (formData.variants || [])[index]
    if (!v) return
    setEditingVariantIndex(index)
    const s = v.selection || {}
    setEditVariantColor(s.color || '')
    setEditColorNameInput(s.colorLabel || (formData.colorNames || {})[s.color] || '')
    setEditVariantSize(s.size || '')
    setEditVariantPriceDelta(v.priceDelta !== null && v.priceDelta !== undefined ? String(v.priceDelta) : '')
    setEditVariantSku(v.sku || '')
    setEditVariantSerialNumber(v.serialNumber || '')
    setEditVariantImages(Array.isArray(v.images) ? v.images : [])
    setEditVariantVideos(Array.isArray(v.videos) ? v.videos : [])
    setEditVariantVideoInput('')
  }

  const cancelEditVariant = () => {
    setEditingVariantIndex(null)
    setEditVariantColor('')
    setEditColorNameInput('')
    setEditVariantSize('')
    setEditVariantPriceDelta('')
    setEditVariantSku('')
    setEditVariantSerialNumber('')
    setEditVariantImages([])
    setEditVariantVideos([])
    setEditVariantVideoInput('')
  }

  const handleUploadEditImages = async (files) => {
    if (!files || files.length === 0) return
    setUploadingEditVariantImages(true)
    try {
      const next = []
      for (const file of Array.from(files)) {
        const uploaded = await uploadImage(file, 'products/variants')
        next.push(uploaded)
      }
      setEditVariantImages(prev => [...prev, ...next])
    } catch (e) {
      setNotification({ type: 'error', message: e.message || 'Error subiendo imágenes' })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setUploadingEditVariantImages(false)
    }
  }

  const removeEditImageAt = (index) => {
    setEditVariantImages(prev => prev.filter((_, i) => i !== index))
  }

  const addEditVariantVideo = () => {
    const v = editVariantVideoInput.trim()
    if (!v) return
    setEditVariantVideos(prev => Array.from(new Set([...prev, v])))
    setEditVariantVideoInput('')
  }

  const removeEditVariantVideo = (index) => {
    setEditVariantVideos(prev => prev.filter((_, i) => i !== index))
  }

  const saveEditedVariant = async () => {
    if (editingVariantIndex === null) return
    if (!editVariantColor || !editVariantSize || !editColorNameInput.trim()) {
      setNotification({ type: 'warning', message: 'Completá color, nombre de color y talle' })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    let finalSku = (editVariantSku || '').trim().toUpperCase()
    if (!finalSku && rules?.productIdentification?.skuAuto?.enabled === true) {
      try {
        const payload = {
          name: formData.name,
          brand: formData.brand,
          collection: formData.collection,
          color: editVariantColor,
          colorName: editColorNameInput.trim(),
          sizes: editVariantSize ? [editVariantSize] : []
        }
        const { sku } = await generateSkuForProduct({ ...payload, id }, rules)
        finalSku = sku.toUpperCase()
      } catch (e) {
        setNotification({ type: 'error', message: e?.message || 'No se pudo generar SKU' })
        setTimeout(() => setNotification(null), 3500)
        return
      }
    }
    const updated = {
      selection: {
        color: editVariantColor,
        colorLabel: editColorNameInput.trim(),
        size: editVariantSize
      },
      priceDelta: editVariantPriceDelta ? Number(editVariantPriceDelta) : null,
      sku: finalSku,
      serialNumber: (editVariantSerialNumber || '').trim(),
      images: editVariantImages,
      videos: editVariantVideos
    }
    setFormData(prev => {
      const nextVariants = (prev.variants || []).map((v, idx) => idx === editingVariantIndex ? updated : v)
      const nextColorNames = {
        ...(prev.colorNames || {}),
        ...(editVariantColor ? { [editVariantColor]: editColorNameInput.trim() } : {})
      }
      return { ...prev, variants: nextVariants, colorNames: nextColorNames }
    })
    setNotification({ type: 'success', message: 'Variante actualizada' })
    setTimeout(() => setNotification(null), 2500)
    cancelEditVariant()
  }

  const validate = () => {
    const next = {}
    if (!formData.name.trim()) next.name = 'Nombre requerido'
    if (!formData.categoryId) next.categoryId = 'Categoría requerida'

    const price = Number(formData.price)
    if (!formData.price || Number.isNaN(price) || price <= 0) next.price = 'Precio debe ser mayor a 0'

    const sp = formData.specialPrice ? Number(formData.specialPrice) : 0
    if (formData.specialPrice && (Number.isNaN(sp) || sp < 0)) next.specialPrice = 'Precio especial inválido'
    if (sp > 0 && sp >= price) next.specialPrice = 'Precio especial debe ser menor al precio'

    if (!formData.shortDescription.trim()) next.shortDescription = 'Descripción corta requerida'
    if (!formData.description || formData.description === '<p><br></p>') next.description = 'Descripción completa requerida'
    if ((formData.variants || []).length > 0) {
      const invalidVariant = (formData.variants || []).some(v => {
        const selection = v?.selection || {}
        return (!selection.color && !selection.size) || (selection.color && !selection.size && (formData.sizes || []).length > 0) || (selection.size && !selection.color && (formData.colors || []).length > 0)
      })
      if (invalidVariant) next.variants = 'Revisá color y talle en las variantes'
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (saving) return
    if (!validate()) return
    setSaving(true)
    try {
      const price = Number(formData.price)
      const specialPrice = formData.specialPrice ? Number(formData.specialPrice) : 0
      const normalizedColors = (formData.colors || []).map(c => String(c).trim()).filter(Boolean)
      const normalizedVariants = (formData.variants || []).map(v => {
        const selection = v?.selection && typeof v.selection === 'object'
          ? {
            color: String(v.selection.color || '').trim(),
            colorLabel: String(v.selection.colorLabel || '').trim(),
            size: String(v.selection.size || '').trim()
          }
          : {
            color: String(v?.color || '').trim(),
            colorLabel: String(v?.colorLabel || '').trim(),
            size: String(v?.size || '').trim()
          }
        return {
          ...v,
          selection,
          priceDelta: v?.priceDelta !== null && v?.priceDelta !== undefined && v?.priceDelta !== '' ? Number(v.priceDelta) : null,
          images: Array.isArray(v?.images) ? v.images : [],
          videos: Array.isArray(v?.videos) ? v.videos : []
        }
      }).filter(v => v.selection.color || v.selection.size || v.sku || v.serialNumber)
      const variantColors = normalizedVariants.map(v => v.selection.color).filter(Boolean)
      const variantSizes = normalizedVariants.map(v => v.selection.size).filter(Boolean)
      const mergedColors = Array.from(new Set([...normalizedColors, ...variantColors]))
      const mergedSizes = Array.from(new Set([...(formData.sizes || []).map(s => String(s).trim()).filter(Boolean), ...variantSizes]))
      const colorNames = Object.fromEntries(
        Object.entries(formData.colorNames || {})
          .map(([key, value]) => [String(key).trim(), String(value || '').trim()])
          .filter(([key, value]) => mergedColors.includes(key) && value)
      )

      const payload = {
        name: formData.name.trim(),
        shortDescription: formData.shortDescription.trim(),
        description: formData.description,
        brand: formData.brand.trim(),
        categoryId: formData.categoryId,
        subcategoryId: formData.subcategoryId || null,
        price,
        specialPrice,
        active: formData.active !== false,
        images: formData.images || [],
        sizes: mergedSizes,
        colors: mergedColors,
        colorNames,
        color: formData.color || (mergedColors[0] || ''),
        material: formData.material.trim(),
        fitType: formData.fitType.trim(),
        season: formData.season.trim(),
        sizeGuide: formData.sizeGuide.trim(),
        sizeChart: (formData.sizeChart || []).map(r => ({
          size: String(r.size || '').trim(),
          chest: String(r.chest || '').trim(),
          waist: String(r.waist || '').trim(),
          length: String(r.length || '').trim()
        })).filter(r => r.size),
        variants: normalizedVariants,
        supplierIds: formData.supplierIds || [],
        tags: formData.tags || [],
        collection: formData.collection.trim(),
        commercialProfile,
        lastUpdatedBy: currentUser?.uid || null,
        lastUpdatedByName: userData?.displayName || null
      }

      if (isEditing) {
        await productsService.update(id, payload)
      } else {
        await productsService.create({
          ...payload,
          createdBy: currentUser?.uid || null,
          createdByName: userData?.displayName || null
        })
      }

      setNotification({ type: 'success', message: isEditing ? 'Producto actualizado' : 'Producto creado' })
      setTimeout(() => setNotification(null), 2000)
      navigate('/admin/productos')
    } catch (e) {
      setNotification({ type: 'error', message: e.message || 'Error guardando producto' })
      setTimeout(() => setNotification(null), 3500)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-sm text-gray-500">Cargando formulario...</div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold">Producto — Indumentaria</h2>
          <p className="text-sm text-gray-500">Rubro activo: {commercialProfile.rubro} / {commercialProfile.subrubro}</p>
        </div>
        <Link className="text-sm font-bold text-gray-500 hover:text-gray-900" to="/admin/productos">
          Volver
        </Link>
      </div>

      <div className="mb-4 overflow-x-auto">
        <div className="flex gap-2">
          {[
            { key: 'general', label: 'General' },
            { key: 'variantes', label: 'Variantes' },
            { key: 'talles', label: 'Talles' },
            { key: 'proveedores', label: 'Proveedores' },
            { key: 'etiquetas', label: 'Etiquetas' }
          ].map(t => (
            <button
              key={t.key}
              type="button"
              className={`px-4 py-2 rounded-xl border text-sm font-bold ${activeTab === t.key ? 'bg-[#c27e70] text-white border-[#c27e70]' : 'bg-white text-gray-700 border-gray-200'}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSave} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-12 space-y-6">
          {activeTab === 'general' ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="space-y-4">
              <h3 className="font-extrabold">Información</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Estado</label>
                  <div className="mt-2 flex items-center gap-3">
                    <input type="checkbox" name="active" checked={formData.active} onChange={handleBasicChange} />
                    <span className="text-sm font-semibold">{formData.active ? 'Publicado' : 'Oculto'}</span>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nombre *</label>
                  <input
                    className={`mt-2 w-full rounded-xl border p-3 text-sm outline-none ${errors.name ? 'border-red-500' : 'border-gray-200'}`}
                    name="name"
                    value={formData.name}
                    onChange={handleBasicChange}
                    placeholder="Nombre del producto"
                  />
                  {errors.name ? <p className="mt-1 text-xs text-red-600 font-semibold">{errors.name}</p> : null}
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Marca</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    name="brand"
                    value={formData.brand}
                    onChange={handleBasicChange}
                    placeholder="Marca"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Colección</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    name="collection"
                    value={formData.collection}
                    onChange={handleBasicChange}
                    placeholder="Ej: Otoño 2026"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              <h3 className="font-extrabold">Descripciones</h3>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Descripción corta *</label>
                <input
                  className={`mt-2 w-full rounded-xl border p-3 text-sm outline-none ${errors.shortDescription ? 'border-red-500' : 'border-gray-200'}`}
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleBasicChange}
                  placeholder="Resumen breve del producto"
                />
                {errors.shortDescription ? <p className="mt-1 text-xs text-red-600 font-semibold">{errors.shortDescription}</p> : null}
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Descripción completa *</label>
                <div className={`mt-2 rounded-xl border ${errors.description ? 'border-red-500' : 'border-gray-200'} overflow-hidden`}>
                  <RichTextEditor value={formData.description} onChange={(value) => setFormData(prev => ({ ...prev, description: value }))} />
                </div>
                {errors.description ? <p className="mt-1 text-xs text-red-600 font-semibold">{errors.description}</p> : null}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              <h3 className="font-extrabold">Precios</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Precio *</label>
                  <input
                    className={`mt-2 w-full rounded-xl border p-3 text-sm outline-none ${errors.price ? 'border-red-500' : 'border-gray-200'}`}
                    name="price"
                    value={formData.price}
                    onChange={handleBasicChange}
                    type="number"
                    min="0"
                    step="0.01"
                  />
                  {errors.price ? <p className="mt-1 text-xs text-red-600 font-semibold">{errors.price}</p> : null}
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Precio especial</label>
                  <input
                    className={`mt-2 w-full rounded-xl border p-3 text-sm outline-none ${errors.specialPrice ? 'border-red-500' : 'border-gray-200'}`}
                    name="specialPrice"
                    value={formData.specialPrice}
                    onChange={handleBasicChange}
                    type="number"
                    min="0"
                    step="0.01"
                  />
                  {errors.specialPrice ? <p className="mt-1 text-xs text-red-600 font-semibold">{errors.specialPrice}</p> : null}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              <h3 className="font-extrabold">Clasificación</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Categoría *</label>
                  <select
                    className={`mt-2 w-full rounded-xl border p-3 text-sm outline-none bg-white ${errors.categoryId ? 'border-red-500' : 'border-gray-200'}`}
                    value={formData.categoryId}
                    onChange={handleCategoryChange}
                  >
                    <option value="">Seleccionar</option>
                    {(categories || []).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {errors.categoryId ? <p className="mt-1 text-xs text-red-600 font-semibold">{errors.categoryId}</p> : null}
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Subcategoría</label>
                  <select
                    className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none bg-white"
                    name="subcategoryId"
                    value={formData.subcategoryId}
                    onChange={handleBasicChange}
                  >
                    <option value="">Sin subcategoría</option>
                    {(subcategories || []).map(sc => (
                      <option key={sc.id} value={sc.id}>{sc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Temporada</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    name="season"
                    value={formData.season}
                    onChange={handleBasicChange}
                    placeholder="Ej: Otoño/Invierno"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Tipo de calce</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    name="fitType"
                    value={formData.fitType}
                    onChange={handleBasicChange}
                    placeholder="Ej: Regular, Slim, Oversize"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Material / Composición</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    name="material"
                    value={formData.material}
                    onChange={handleBasicChange}
                    placeholder="Ej: 100% algodón"
                  />
                </div>
              </div>
            </div>
          </div>
          ) : null}

          {activeTab === 'variantes' ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-extrabold">Variantes (Color + Talle)</h3>
            {errors.variants ? <p className="text-xs text-red-600 font-semibold">{errors.variants}</p> : null}
            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Color</label>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-2 items-center">
                    <input
                      className="h-11 w-16 rounded-xl border border-gray-200"
                      type="color"
                      value={variantColor || '#c27e70'}
                      onChange={(e) => {
                        const next = e.target.value
                        setVariantColor(next)
                        if ((formData.colorNames || {})[next]) {
                          setColorNameInput((formData.colorNames || {})[next])
                        }
                      }}
                    />
                    <input
                      className="h-11 rounded-xl border border-gray-200 p-3 text-sm outline-none"
                      value={colorNameInput}
                      onChange={(e) => setColorNameInput(e.target.value)}
                      placeholder="Nombre del color"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Talle</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    value={variantSize}
                    onChange={(e) => setVariantSize(e.target.value.toUpperCase())}
                    placeholder="Ej: M"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Precio adicional</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    value={variantPriceDelta}
                    onChange={(e) => setVariantPriceDelta(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">SKU de variante</label>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                      value={variantSku}
                      onChange={(e) => setVariantSku(e.target.value)}
                      placeholder="SKU"
                    />
                    {rules?.productIdentification?.skuAuto?.enabled === true ? (
                      <button
                        type="button"
                        className="px-3 py-2 text-xs font-bold rounded-xl border border-gray-200 hover:border-gray-300 bg-white"
                        onClick={async () => {
                          try {
                            const payload = {
                              name: formData.name,
                              brand: formData.brand,
                              collection: formData.collection,
                              color: variantColor,
                              colorName: colorNameInput.trim() || (formData.colorNames || {})[variantColor] || '',
                              sizes: variantSize ? [variantSize] : []
                            }
                            const { sku } = await generateSkuForProduct({ ...payload, id }, rules)
                            setVariantSku(sku.toUpperCase())
                            setNotification({ type: 'success', message: 'SKU de variante generado' })
                            setTimeout(() => setNotification(null), 2500)
                          } catch (e) {
                            setNotification({ type: 'error', message: e?.message || 'No se pudo generar SKU' })
                            setTimeout(() => setNotification(null), 3500)
                          }
                        }}
                        disabled={!variantColor || !variantSize || !colorNameInput.trim()}
                        title="Generar SKU"
                      >
                        ↻
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Número de serie</label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    value={variantSerialNumber}
                    onChange={(e) => setVariantSerialNumber(e.target.value)}
                    placeholder="Serie"
                  />
                  {rules?.productIdentification?.serialAuto?.enabled === true ? (
                    <button
                      type="button"
                      className="px-3 py-2 text-xs font-bold rounded-xl border border-gray-200 hover:border-gray-300 bg-white"
                      onClick={async () => {
                        try {
                          const digits = rules?.productIdentification?.serialAuto?.digits ?? 15
                          const serial = await generateSerialNumber(digits)
                          setVariantSerialNumber(serial)
                          setNotification({ type: 'success', message: 'Serie generada' })
                          setTimeout(() => setNotification(null), 2500)
                        } catch (e) {
                          setNotification({ type: 'error', message: e?.message || 'No se pudo generar número de serie' })
                          setTimeout(() => setNotification(null), 3500)
                        }
                      }}
                      title="Generar número de serie"
                    >
                      ↻
                    </button>
                  ) : null}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Imágenes de la variante</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="mt-2 block w-full text-sm"
                  onChange={(e) => handleUploadImages(e.target.files)}
                  disabled={uploadingVariantImages}
                />
                {uploadingVariantImages ? <p className="text-sm text-gray-500">Subiendo imágenes...</p> : null}
                <div className="grid grid-cols-2 gap-3">
                  {variantImages.map((img, idx) => (
                    <div key={(img.fileId || img.url || idx) + idx} className="relative rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                      <img className="w-full h-32 object-cover" src={img.thumbnailUrl || img.url} alt={`Imagen ${idx + 1}`} />
                      <button
                        type="button"
                        className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-600 text-xs font-bold px-2 py-1 rounded-lg"
                        onClick={() => removeImageAt(idx)}
                      >
                        Quitar
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Videos embebidos</label>
                <div className="mt-2 flex gap-2">
                  <input
                    className="flex-1 rounded-xl border border-gray-200 p-3 text-sm outline-none"
                    value={variantVideoInput}
                    onChange={(e) => setVariantVideoInput(e.target.value)}
                    placeholder="https://www.youtube.com/embed/..."
                  />
                  <button type="button" className="rounded-xl bg-gray-900 text-white px-4 text-sm font-bold" onClick={addVariantVideo}>
                    Agregar
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {variantVideos.map((video, idx) => (
                    <button
                      key={`${video}-${idx}`}
                      type="button"
                      className="px-3 py-1 rounded-full border border-gray-200 text-xs font-bold hover:border-red-300"
                      onClick={() => removeVariantVideo(idx)}
                      title="Quitar"
                    >
                      {video}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="rounded-xl bg-[#181211] text-white text-sm font-bold py-3"
                onClick={handleAddVariant}
              >
                Agregar variante
              </button>
            </div>

            {(formData.variants || []).length > 0 ? (
              <div className="border border-gray-100 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-7 bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500 px-4 py-3">
                  <span>Color</span>
                  <span>Talle</span>
                  <span>SKU</span>
                  <span>Serie</span>
                  <span>Medios</span>
                  <span>Stock</span>
                  <span></span>
                </div>
                {(formData.variants || []).map((variant, idx) => {
                  const color = variant.selection?.color || ''
                  const colorLabel = variant.selection?.colorLabel || (formData.colorNames || {})[color] || color
                  return (
                    <div key={`${variant.sku || color || idx}-${idx}`} className="grid grid-cols-7 px-4 py-3 text-sm border-t border-gray-100 items-center">
                      <span className="font-semibold">{colorLabel || '-'}</span>
                      <span>{variant.selection?.size || '-'}</span>
                      <span className="text-xs break-all">{variant.sku || '-'}</span>
                      <span className="text-xs break-all">{variant.serialNumber || '-'}</span>
                      <span className="text-xs">{(variant.images || []).length} img · {(variant.videos || []).length} vid</span>
                      <span className="text-xs font-semibold text-emerald-700">
                        {isEditing ? (
                          <button type="button" className="bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-3 py-1 rounded-lg font-bold transition-colors" onClick={() => openStockModal(variant, idx)}>
                            + Stock
                          </button>
                        ) : '-'}
                      </span>
                      <div className="flex items-center gap-2">
                        <button type="button" className="text-xs font-bold text-emerald-700" onClick={() => beginEditVariant(idx)}>
                          Editar
                        </button>
                        <button type="button" className="text-xs font-bold text-red-600" onClick={() => removeVariantAt(idx)}>
                          Quitar
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : null}
            {editingVariantIndex !== null ? (
              <div className="mt-4 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-4">
                <h4 className="font-extrabold">Editar variante</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Color</label>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-2 items-center">
                      <input
                        className="h-11 w-16 rounded-xl border border-gray-200"
                        type="color"
                        value={editVariantColor || '#c27e70'}
                        onChange={(e) => {
                          const next = e.target.value
                          setEditVariantColor(next)
                          if ((formData.colorNames || {})[next]) {
                            setEditColorNameInput((formData.colorNames || {})[next])
                          }
                        }}
                      />
                      <input
                        className="h-11 rounded-xl border border-gray-200 p-3 text-sm outline-none"
                        value={editColorNameInput}
                        onChange={(e) => setEditColorNameInput(e.target.value)}
                        placeholder="Nombre del color"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Talle</label>
                    <input
                      className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                      value={editVariantSize}
                      onChange={(e) => setEditVariantSize(e.target.value.toUpperCase())}
                      placeholder="Ej: M"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Precio adicional</label>
                    <input
                      className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                      value={editVariantPriceDelta}
                      onChange={(e) => setEditVariantPriceDelta(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">SKU de variante</label>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                        value={editVariantSku}
                        onChange={(e) => setEditVariantSku(e.target.value)}
                        placeholder="SKU"
                      />
                      {rules?.productIdentification?.skuAuto?.enabled === true ? (
                        <button
                          type="button"
                          className="px-3 py-2 text-xs font-bold rounded-xl border border-gray-200 hover:border-gray-300 bg-white"
                          onClick={async () => {
                            try {
                              const payload = {
                                name: formData.name,
                                brand: formData.brand,
                                collection: formData.collection,
                                color: editVariantColor,
                                colorName: editColorNameInput.trim(),
                                sizes: editVariantSize ? [editVariantSize] : []
                              }
                              const { sku } = await generateSkuForProduct({ ...payload, id }, rules)
                              setEditVariantSku(sku.toUpperCase())
                              setNotification({ type: 'success', message: 'SKU de variante generado' })
                              setTimeout(() => setNotification(null), 2500)
                            } catch (e) {
                              setNotification({ type: 'error', message: e?.message || 'No se pudo generar SKU' })
                              setTimeout(() => setNotification(null), 3500)
                            }
                          }}
                          disabled={!editVariantColor || !editVariantSize || !editColorNameInput.trim()}
                          title="Generar SKU"
                        >
                          ↻
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Número de serie</label>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                      value={editVariantSerialNumber}
                      onChange={(e) => setEditVariantSerialNumber(e.target.value)}
                      placeholder="Serie"
                    />
                    {rules?.productIdentification?.serialAuto?.enabled === true ? (
                      <button
                        type="button"
                        className="px-3 py-2 text-xs font-bold rounded-xl border border-gray-200 hover:border-gray-300 bg-white"
                        onClick={async () => {
                          try {
                            const digits = rules?.productIdentification?.serialAuto?.digits ?? 15
                            const serial = await generateSerialNumber(digits)
                            setEditVariantSerialNumber(serial)
                            setNotification({ type: 'success', message: 'Serie generada' })
                            setTimeout(() => setNotification(null), 2500)
                          } catch (e) {
                            setNotification({ type: 'error', message: e?.message || 'No se pudo generar número de serie' })
                            setTimeout(() => setNotification(null), 3500)
                          }
                        }}
                        title="Generar número de serie"
                      >
                        ↻
                      </button>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Imágenes de la variante</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="mt-2 block w-full text-sm"
                    onChange={(e) => handleUploadEditImages(e.target.files)}
                    disabled={uploadingEditVariantImages}
                  />
                  {uploadingEditVariantImages ? <p className="text-sm text-gray-500">Subiendo imágenes...</p> : null}
                  <div className="grid grid-cols-2 gap-3">
                    {editVariantImages.map((img, idx) => (
                      <div key={(img.fileId || img.url || idx) + idx} className="relative rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                        <img className="w-full h-32 object-cover" src={img.thumbnailUrl || img.url} alt={`Imagen ${idx + 1}`} />
                        <button
                          type="button"
                          className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-600 text-xs font-bold px-2 py-1 rounded-lg"
                          onClick={() => removeEditImageAt(idx)}
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Videos embebidos</label>
                  <div className="mt-2 flex gap-2">
                    <input
                      className="flex-1 rounded-xl border border-gray-200 p-3 text-sm outline-none"
                      value={editVariantVideoInput}
                      onChange={(e) => setEditVariantVideoInput(e.target.value)}
                      placeholder="https://www.youtube.com/embed/..."
                    />
                    <button type="button" className="rounded-xl bg-gray-900 text-white px-4 text-sm font-bold" onClick={addEditVariantVideo}>
                      Agregar
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {editVariantVideos.map((video, idx) => (
                      <button
                        key={`${video}-${idx}`}
                        type="button"
                        className="px-3 py-1 rounded-full border border-gray-200 text-xs font-bold hover:border-red-300"
                        onClick={() => removeEditVariantVideo(idx)}
                        title="Quitar"
                      >
                        {video}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button type="button" className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold" onClick={cancelEditVariant}>
                    Cancelar
                  </button>
                  <button type="button" className="rounded-xl bg-[#181211] text-white text-sm font-bold px-4 py-2" onClick={saveEditedVariant}>
                    Guardar cambios
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          ) : null}

          {activeTab === 'talles' ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-extrabold">Guía y tabla de talles</h3>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Guía de talles</label>
              <input
                className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none"
                name="sizeGuide"
                value={formData.sizeGuide}
                onChange={handleBasicChange}
                placeholder="URL o texto de referencia"
              />
            </div>

            <div className="rounded-2xl border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-4 bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500 px-4 py-3">
                <span>Talle</span>
                <span>Pecho</span>
                <span>Cintura</span>
                <span>Largo</span>
              </div>
              {(formData.sizeChart || []).length === 0 ? (
                <div className="px-4 py-4 text-sm text-gray-500">Sin filas cargadas.</div>
              ) : (
                (formData.sizeChart || []).map((r, idx) => (
                  <div key={idx} className="grid grid-cols-4 px-4 py-3 text-sm border-t border-gray-100 items-center">
                    <span className="font-bold">{r.size}</span>
                    <span>{r.chest}</span>
                    <span>{r.waist}</span>
                    <span className="flex items-center justify-between gap-2">
                      {r.length}
                      <button type="button" className="text-xs font-bold text-red-600" onClick={() => removeSizeChartRow(idx)}>
                        Quitar
                      </button>
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <input className="md:col-span-1 rounded-xl border border-gray-200 p-3 text-sm outline-none" placeholder="Talle" value={sizeChartRow.size} onChange={(e) => setSizeChartRow(prev => ({ ...prev, size: e.target.value }))} />
              <input className="md:col-span-1 rounded-xl border border-gray-200 p-3 text-sm outline-none" placeholder="Pecho" value={sizeChartRow.chest} onChange={(e) => setSizeChartRow(prev => ({ ...prev, chest: e.target.value }))} />
              <input className="md:col-span-1 rounded-xl border border-gray-200 p-3 text-sm outline-none" placeholder="Cintura" value={sizeChartRow.waist} onChange={(e) => setSizeChartRow(prev => ({ ...prev, waist: e.target.value }))} />
              <input className="md:col-span-1 rounded-xl border border-gray-200 p-3 text-sm outline-none" placeholder="Largo" value={sizeChartRow.length} onChange={(e) => setSizeChartRow(prev => ({ ...prev, length: e.target.value }))} />
              <button type="button" className="md:col-span-1 rounded-xl bg-gray-900 text-white text-sm font-bold" onClick={addSizeChartRow}>
                Agregar fila
              </button>
            </div>
          </div>
          ) : null}
        </div>

        <div className="lg:col-span-12 space-y-6">
          {activeTab === 'proveedores' ? (
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-extrabold text-lg text-gray-900 border-b-2 border-amber-500 pb-2">Proveedores</h3>
            {loadingSuppliers ? (
              <p className="text-sm text-gray-500">Cargando proveedores...</p>
            ) : (
              <SupplierCards
                suppliers={suppliers}
                selectedIds={formData.supplierIds}
                onChange={(selectedIds) => {
                  setFormData(prev => ({ ...prev, supplierIds: selectedIds }))
                }}
                disabled={saving}
              />
            )}
          </div>
          ) : null}

          {activeTab === 'etiquetas' ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-extrabold">Etiquetas</h3>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded-xl border border-gray-200 p-3 text-sm outline-none"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Ej: sostenible"
              />
              <button type="button" className="rounded-xl bg-gray-900 text-white px-4 text-sm font-bold" onClick={addTag}>
                Agregar
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(formData.tags || []).map(t => (
                <button
                  key={t}
                  type="button"
                  className="px-3 py-1 rounded-full border border-gray-200 text-xs font-bold hover:border-red-300"
                  onClick={() => removeTag(t)}
                  title="Quitar"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <button
              type="submit"
              className="h-11 px-5 rounded-xl bg-[#c27e70] hover:bg-[#a66a5e] text-white font-extrabold text-sm disabled:opacity-60"
              disabled={saving || uploadingVariantImages}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </form>

      {stockModalData && (
        <VariantStockModal
          isOpen={!!stockModalData}
          onClose={() => setStockModalData(null)}
          variant={stockModalData.variant}
          variantIndex={stockModalData.variantIndex}
          productId={stockModalData.productId}
          onStockSaved={() => setStockModalData(null)}
        />
      )}
    </div>
  )
}

export default ProductFormFashion
